import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ConversationList = ({ 
  conversations, 
  activeConversationId, 
  onSelectConversation,
  searchQuery,
  onSearchChange 
}) => {
  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const messageDate = new Date(timestamp);
    const diffInMinutes = Math.floor((now - messageDate) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return messageDate?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  const truncateMessage = (text, maxLength = 40) => {
    if (text?.length <= maxLength) return text;
    return text?.substring(0, maxLength) + '...';
  };

  const filteredConversations = conversations?.filter(conv =>
    conv?.userName?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full bg-card">
      <div className="p-4 border-b border-border">
        <h2 className="text-lg md:text-xl font-heading font-semibold mb-3">Messages</h2>
        <div className="relative">
          <Icon 
            name="Search" 
            size={18} 
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
          />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 bg-muted rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-smooth"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filteredConversations?.length > 0 ? (
          <div className="divide-y divide-border">
            {filteredConversations?.map((conversation) => (
              <button
                key={conversation?.id}
                onClick={() => onSelectConversation(conversation?.id)}
                className={`w-full p-4 flex items-start gap-3 transition-smooth hover:bg-muted ${
                  activeConversationId === conversation?.id ? 'bg-primary/10' : ''
                } ${conversation?.unreadCount > 0 ? 'bg-primary/5' : ''}`}
              >
                <div className="relative flex-shrink-0">
                  <Image
                    src={conversation?.avatar}
                    alt={conversation?.avatarAlt}
                    className={`w-12 h-12 md:w-14 md:h-14 rounded-full object-cover ${
                      conversation?.photoBlurred ? 'blur-md' : ''
                    }`}
                  />
                  {conversation?.isOnline && (
                    <span className="absolute bottom-0 right-0 w-3 h-3 md:w-4 md:h-4 bg-success rounded-full border-2 border-card" />
                  )}
                  {conversation?.unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-accent text-[10px] font-bold text-accent-foreground">
                      {conversation?.unreadCount > 9 ? '9+' : conversation?.unreadCount}
                    </span>
                  )}
                </div>

                <div className="flex-1 min-w-0 text-left">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm md:text-base font-medium ${
                        conversation?.unreadCount > 0 ? 'text-foreground' : 'text-muted-foreground'
                      }`}>
                        {conversation?.userName}
                      </span>
                      {conversation?.isVerified && (
                        <Icon name="BadgeCheck" size={14} color="var(--color-primary)" />
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {formatTimestamp(conversation?.lastMessageTime)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {conversation?.lastMessageType === 'image' && (
                      <Icon name="Image" size={14} className="text-muted-foreground flex-shrink-0" />
                    )}
                    {conversation?.lastMessageType === 'disappearing' && (
                      <Icon name="Timer" size={14} className="text-warning flex-shrink-0" />
                    )}
                    <p className={`text-xs md:text-sm ${
                      conversation?.unreadCount > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'
                    }`}>
                      {truncateMessage(conversation?.lastMessage)}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <Icon name="MessageCircleOff" size={48} color="var(--color-muted-foreground)" className="mb-3" />
            <p className="text-sm md:text-base text-muted-foreground mb-1">No conversations found</p>
            <p className="text-xs md:text-sm text-muted-foreground">
              {searchQuery ? 'Try a different search term' : 'Start a conversation to see messages here'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationList;